import {
  loadingAtom,
  planAtom,
  filteredPlansAtom,
  fetchPlanAtom,
  fetchPlansAtom,
  refetchPlansAtom,
  channelAtom,
  planTokenAtom,
  userAtom,
  isEditingStepAtom,
} from "../state/"
import { useAtom, useSetAtom } from "jotai"
import { useNavigate, useParams } from "react-router-dom"
import { useState, useEffect, useCallback } from "react"
import DeleteConfirmation from "../Components/DeleteConfirmation"
import MentorWelcome from "../Partials/MentorWelcome"
import Page from "../Components/Page"
import SideBar from "../Components/SideBar"
import { Outlet } from "react-router-dom"
import { useDownloadPdf } from "../hooks/useDownloadPdf"

function Plan() {
  const [sideBarOpen, setSideBarOpen] = useState(window.innerWidth >= 768)
  const [addPlanOpen, setAddPlanOpen] = useState(false)
  const [editPlanOpen, setEditPlanOpen] = useState(false)
  const [noPlans, setNoPlans] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)

  const [loading] = useAtom(loadingAtom)
  const [plan] = useAtom(planAtom)
  const [filteredPlans, setFilteredPlans] = useAtom(filteredPlansAtom)
  const [channel] = useAtom(channelAtom)
  const [planToken] = useAtom(planTokenAtom)
  const [user] = useAtom(userAtom)
  const [isEditingStep] = useAtom(isEditingStepAtom)

  const fetchPlan = useSetAtom(fetchPlanAtom)
  const fetchPlans = useSetAtom(fetchPlansAtom)
  const refetchPlans = useSetAtom(refetchPlansAtom)
  const navigate = useNavigate()
  const { token } = useParams()
  const { downloadPdf } = useDownloadPdf()

  // Memoize functions to prevent infinite loops in child components
  const getPlan = useCallback(() => {
    if (token) {
      return fetchPlan(token)
    }
    return Promise.resolve()
  }, [token, fetchPlan])

  const refreshPlans = useCallback(() => {
    refetchPlans()
  }, [refetchPlans])

  useEffect(() => {
    fetchPlans({ token, navigate, setJoinOpen, setNoPlans })
  }, [])

  useEffect(() => {
    if (token) {
      fetchPlan(token)
    }
  }, [token])

  // Listen for plan updates via Pusher
  useEffect(() => {
    if (channel && planToken && user) {
      const handlePlanUpdate = (data) => {
        // Always refetch on plan status changes — these affect navigation and UI
        // structure and must be reflected immediately regardless of who triggered them.
        // For content changes, skip self-triggered events to avoid interrupting edits.
        const isPlanStatusChange = data.step === "planStatus"
        if (isPlanStatusChange || data.user_id != user.id) {
          // Don't refresh the entire plan if someone is actively editing a step
          // The Step component will handle pending updates and apply them when appropriate
          if (!isEditingStep) {
            fetchPlan(planToken)
          }
        }
      }

      channel.bind("plan." + planToken, handlePlanUpdate)

      return () => {
        channel.unbind("plan." + planToken, handlePlanUpdate)
      }
    }
  }, [channel, planToken, user, fetchPlan, isEditingStep])

  // Check if user has pending invitation and redirect to invitation page
  useEffect(() => {
    if (plan && plan.current_user_invitation_status === "pending" && token) {
      // Don't redirect if already on invitation page
      if (!window.location.pathname.endsWith("/invitation")) {
        navigate(`/plan/${token}/invitation`, { replace: true })
      }
    }
  }, [plan, token, navigate])

  const onSearchChange = e => {
    const search = e.target.value
    setFilteredPlans(search)
  }

  const onPlanAdd = plan => {
    setAddPlanOpen(false)
    navigate(`/plan/${plan.token}`)
  }

  const onPlanEdit = () => {
    setEditPlanOpen(false)
    fetchPlan(token)
    refetchPlans()
  }

  const onPlanDelete = () => {
    fetchPlans({ token, navigate, setJoinOpen, setNoPlans })
    setEditPlanOpen(false)
    navigate("/")
  }

  const onDownload = () => {
    downloadPdf(token, plan.name)
  }

  return (
    <>
      <MentorWelcome showWelcome={showWelcome} setShowWelcome={setShowWelcome} />
      <Page
        sidebar={
          <SideBar
            filteredPlans={filteredPlans}
            onSearchChange={onSearchChange}
            sideBarOpen={sideBarOpen}
            setSideBarOpen={setSideBarOpen}
            noPlans={noPlans}
            addPlanOpen={addPlanOpen}
            setAddPlanOpen={setAddPlanOpen}
            onPlanAdd={onPlanAdd}
          />
        }
      >
        <Outlet
          context={{
            plan,
            getPlan,
            refreshPlans,
            noPlans,
            editPlanOpen,
            setEditPlanOpen,
            onPlanEdit,
            onPlanDelete,
            inviteOpen,
            setInviteOpen,
            onDownload,
          }}
        />
      </Page>
      <DeleteConfirmation />
    </>
  )
}

export default Plan
